import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';

import { api } from '@/api';
import { API_URL } from '@/dtos/generated-paths';
import {
  IReturnedCheckDtoCheckTypeEnum,
  IReturnedItemsDto,
  IReturnMailDtoOriginalMailingTypeEnum,
} from '@/dtos/models';
import { useAuthStore } from '@/modules/user/stores';

const QUERY_KEY = 'LOCKBOXES_RETURNED_ITEMS_QUERY';

const QUALIFYING_TYPES = [
  IReturnMailDtoOriginalMailingTypeEnum.WELCOME_LETTER,
  IReturnMailDtoOriginalMailingTypeEnum.ORPHAN_LETTER,
  IReturnMailDtoOriginalMailingTypeEnum.STATEMENT,
];

const useReturnedItemsQuery = () => {
  const auth = useAuthStore((state) => state.auth);

  const { data, isLoading, isSuccess, refetch } = useQuery(
    [QUERY_KEY],
    () => api.get<IReturnedItemsDto>(API_URL.LOCKBOXES_RETURNED_ITEMS),
    {
      enabled: !!auth,
    });

  const returnedMails = useMemo(() => data?.data?.returned_mails ?? [], [data]);
  const returnedChecks = useMemo(() => data?.data?.returned_checks ?? [], [data]);

  const hasQualifyingLetters = useMemo(
    () => returnedMails.some(
      (mail) => !!mail?.original_mailing_type && QUALIFYING_TYPES.includes(mail.original_mailing_type)
    ),
    [returnedMails]
  );

  const providerChecksCount = useMemo(
    () => returnedChecks.filter(
      (check) => check?.check_type === IReturnedCheckDtoCheckTypeEnum.PROVIDER
    ).length,
    [returnedChecks]
  );

  const hasReturnedItems = useMemo(
    () => returnedMails.length > 0 || returnedChecks.length > 0,
    [returnedMails, returnedChecks]
  );

  const isProviderOnly = useMemo(
    () => returnedMails.length === 0
      && returnedChecks.length > 0
      && providerChecksCount === returnedChecks.length,
    [returnedMails, returnedChecks, providerChecksCount]
  );

  return {
    returnedMails,
    returnedChecks,
    hasQualifyingLetters,
    hasReturnedItems,
    providerChecksCount,
    isProviderOnly,
    isLoading,
    isSuccess,
    refetch,
  };
};

export default useReturnedItemsQuery;
export { QUERY_KEY as LOCKBOXES_RETURNED_ITEMS_QUERY_KEY, QUALIFYING_TYPES };
